import React from 'react';
import {EntityListColumn} from 'components';
import {getDateColumn} from 'helpers';
import {TFunction} from 'react-i18next';
import {SetOptional} from 'type-fest';
import {
	ArrayValues,
	DisassemblyStatusField,
	RenderFn,
	StatusField,
	renderDate,
	renderPhase,
	renderRegulatoryDocumentStatus,
} from 'components/EntityList/ColumnRenderers';
import enums from '../../../../locales/common/enums.de.json';
import {createSubfieldTranslationKey} from 'features/localizedTooltips';
import {renderKeywordsCell} from 'components/Keywords/Keywords';
import {ARRAY_TYPE, DATE_TYPE} from 'components/EntityList/EntityUtils';
import {TooltipHost} from '@fluentui/react';
import {PogisDocument, RegulatoryDocument, WorkflowStatus} from 'types';

export const getRegulatoryDocumentsListBaseColumns = (
	t: TFunction,
	tEnum: TFunction,
	tPogis: TFunction,
	showWorkflowStatus = false,
): EntityListColumn[] => {
	type Enums = typeof enums;
	type EnumKey = keyof Enums;

	type EntityListColumnFieldsWithOptionalOnes = SetOptional<
		EntityListColumn,
		'minWidth' | 'key'
	>;

	/**
	 * We shouldn't be able to provide the name because it doesn't make sense to
	 * provide a translation key for the name and provide the name simultaneously.
	 */
	type FieldsFromEntityListColumn = Omit<
		EntityListColumnFieldsWithOptionalOnes,
		'name'
	>;

	interface StatusColumnFields extends FieldsFromEntityListColumn {
		translationKeyForName: string;
		keyWithoutPrefix: string;
		/**
		 * The key of the object with the translations. The object must contain a
		 * translation for each possible field value.
		 */
		fieldPathToTranslationsInEnumTranslationFile: EnumKey;
		filterable: boolean;
	}

	const createCommonStatusColumnFields = ({
		translationKeyForName,
		keyWithoutPrefix,
		fieldPathToTranslationsInEnumTranslationFile: enumKey,
		...other
	}: StatusColumnFields): EntityListColumn => {
		return {
			key: `rd-${keyWithoutPrefix}`,
			name: t(translationKeyForName),
			getFilterLabel: value => tEnum(`${enumKey}.${value}`),
			isMultiline: true,
			minWidth: 100,
			maxWidth: 100,
			isResizable: true,
			sortable: true,
			...other,
		};
	};

	const workflowStatusEnumKey = 'WorkflowStatus';
	const disassemblyStatusEnumKey = 'RegulatoryDocumentDisassemblyStatus';

	const renderWorkflowStatus: RenderFn = (item, index, column) => {
		// QualityControl status is missing ?
		const getTooltipContent = () => {
			switch (item?.workflow?.status) {
				case WorkflowStatus.New:
					return tEnum('WorkflowStatusTooltip.NEW');
				case WorkflowStatus.InProgressInternal:
					return tEnum('WorkflowStatusTooltip.IN_PROGRESS_INTERNAL');
				case WorkflowStatus.InProgressExternal:
					return tEnum('WorkflowStatusTooltip.IN_PROGRESS_EXTERNAL');
				case WorkflowStatus.Clearing:
					return tEnum('WorkflowStatusTooltip.CLEARING');
				case WorkflowStatus.Finalized:
					return tEnum('WorkflowStatusTooltip.FINALIZED');
				default:
					return '';
			}
		};
		return (
			<TooltipHost content={getTooltipContent()}>
				<StatusField
					item={item}
					i={index}
					col={column}
					value={item?.workflow?.status}
					enumName={workflowStatusEnumKey}
				/>
			</TooltipHost>
		);
	};

	const renderDisassemblyStatus: RenderFn = (item, index, column) => {
		return (
			<DisassemblyStatusField
				item={item}
				i={index}
				col={column}
				value={item?.disassemblyStatus}
				enumName={disassemblyStatusEnumKey}
			/>
		);
	};

	const createWorkflowStatusColumn = (): EntityListColumn => {
		return createCommonStatusColumnFields({
			translationKeyForName: 'WorkflowStatus',
			keyWithoutPrefix: 'workflowStatus',
			fieldPathToTranslationsInEnumTranslationFile: workflowStatusEnumKey,
			tooltipHostProps: {
				translationKey: createSubfieldTranslationKey('workflow.status'),
			},
			onRender: renderWorkflowStatus,
			filterable: false,
		});
	};

	const createDisassemblyStatusColumn = (): EntityListColumn => {
		return createCommonStatusColumnFields({
			translationKeyForName: 'DisassemblyStatus',
			keyWithoutPrefix: 'disassemblyStatus',
			fieldPathToTranslationsInEnumTranslationFile: disassemblyStatusEnumKey,
			tooltipHostProps: {
				translationKey: createSubfieldTranslationKey('disassembly.status'),
			},
			minWidth: 180,
			onRender: renderDisassemblyStatus,
			filterable: false,
		});
	};

	const pogisColumn = getPogisCommnunicatedCol(tPogis);

	const getColumnsBeforeWorkflowStatus = (): EntityListColumn[] => {
		return [
			{
				key: 'rd-name',
				name: t('Version'),
				fieldName: 'name',
				isMultiline: true,
				minWidth: 150,
				maxWidth: 150,
				isResizable: true,
				sortable: true,
			},
			pogisColumn,

			createCommonStatusColumnFields({
				translationKeyForName: 'Status',
				keyWithoutPrefix: 'status',
				fieldPathToTranslationsInEnumTranslationFile:
					'RegulatoryDocumentStatus',
				fieldName: 'status',
				onRender: renderRegulatoryDocumentStatus(),
				filterable: false,
			}),
		];
	};

	const getInitialColumnsWithWorkflowStatus = (
		initialColumns: EntityListColumn[],
	): EntityListColumn[] => {
		const workflowStatusColumn: EntityListColumn = createWorkflowStatusColumn();
		const disassemblyStatusColumn: EntityListColumn =
			createDisassemblyStatusColumn();
		return [...initialColumns, disassemblyStatusColumn, workflowStatusColumn];
	};

	const getColumnsUpToAndPossiblyIncludingWorkflowStatus =
		(): EntityListColumn[] => {
			const initialColumns: EntityListColumn[] =
				getColumnsBeforeWorkflowStatus();
			if (showWorkflowStatus)
				return getInitialColumnsWithWorkflowStatus(initialColumns);
			return initialColumns;
		};

	const initialColumnsWithPossibleWorkflowStatusColumn: EntityListColumn[] =
		getColumnsUpToAndPossiblyIncludingWorkflowStatus();

	return [
		...initialColumnsWithPossibleWorkflowStatusColumn,
		{
			key: 'rd-keywords',
			name: t('Keywords'),
			fieldName: 'keywords',
			minWidth: 200,
			maxWidth: 200,
			filterable: true,
			dataType: ARRAY_TYPE,
			onRender: renderKeywordsCell,
		},
		getDateColumn(
			'p-dateNewRegistration',
			t('DateNewRegistration'),
			'dateNewRegistration',
			true,
		),
		getDateColumn('p-dateNewTypes', t('DateNewTypes'), 'dateNewTypes', true),
		getDateColumn('p-dateEffective', t('DateEffective'), 'dateEffective', true),
		{
			key: 'rd-modelYear',
			name: t('ModelYear'),
			fieldName: 'modelYear',
			minWidth: 100,
			isResizable: true,
			filterable: true,
			sortable: true,
		},
		{
			key: 'rd-phaseIn',
			name: t('PhaseIn'),
			fieldName: 'phaseIn',
			minWidth: 100,
			maxWidth: 100,
			isResizable: true,
			onRender: renderPhase(),
		},
		{
			key: 'rd-phaseOut',
			name: t('PhaseOut'),
			fieldName: 'phaseOut',
			minWidth: 100,
			maxWidth: 100,
			isResizable: true,
			onRender: renderPhase(),
		},
		getDateColumn(
			'p-dateExpiration',
			t('DateExpiration'),
			'dateExpiration',
			true,
		),
		{
			key: 'rd-modifiedAt',
			name: t('ModifiedAt'),
			fieldName: 'modifiedAt',
			filterable: true,
			isMultiline: false,
			minWidth: 100,
			maxWidth: 100,
			isResizable: true,
			onRender: renderDate(),
			dataType: DATE_TYPE,
			sortable: true,
		},
	];
};

const getPogisCommnunicatedCol = (t: TFunction) => {
	const renderPogisDocument = (item: RegulatoryDocument) => {
		const array = (item.pogisDocuments ?? []).map(x => x.pogisId);
		return <ArrayValues array={array} />;
	};

	return {
		key: 'rd-pogis',
		name: t('AlreadyCommunicated'),
		fieldName: 'pogisDocuments',
		onRender: renderPogisDocument,
		isMultiline: true,
		filterable: true,
		getFilterLabel: (x: PogisDocument) => x.pogisId,
		minWidth: 100,
		maxWidth: 100,
		isResizable: true,
		dataType: ARRAY_TYPE,
	};
};
